import { ProjectService } from '@/api/project';
import { Loading } from '@/components/Loading';
import { Section } from '@/components/Section';
import { CreateButton } from '@/components/shared/button/CreateButton';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import {
  ExtendedTableLowCode,
  ExtendedTableLowCodeRef,
} from '@/components/shared/table/ExtendedTableLowCode';
import { CustomCell } from '@/components/testRun/CustomCell';
import { DeleteTestRun } from '@/components/testRun/DeleteRunTest';
import { useAccess } from '@/utils/hooks/useAccess';
import { useCustomSpec } from '@/utils/hooks/useCustomSpec';
import { useIsExternal } from '@/utils/hooks/useUserType';
import React, { useRef } from 'react';
import { FaArchive } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { IFilterSpec } from '@/components/shared/filter/TableFilterPanel';
import { ITestRunSchema, TestRunService } from '@/api/testRun';
import { LinkButton } from '@/components/shared/button/LinkButton';
import { FaPen } from 'react-icons/fa6';
import { BiSolidShow } from 'react-icons/bi';
import { HideCustomCreateButton } from '@/components/shared/button/HideCustomButtonCreate';

interface CustomActionCellProps {
  data: ITestRunSchema;
  isReadOnly?: boolean;
  tableRef: React.RefObject<ExtendedTableLowCodeRef>;
}

export const CustomActionCell: React.FC<CustomActionCellProps> = ({
  data,
  isReadOnly,
  tableRef,
}) => {
  return !isReadOnly ? (
    <>
      <LinkButton path={data.id} icon={<BiSolidShow size={20} />} />

      <LinkButton path={`${data.id}/edit`} icon={<FaPen size={15} />} />

      <DeleteTestRun
        id={data.id!}
        onSuccess={() => tableRef.current?.refetch()}
      />
    </>
  ) : (
    <LinkButton path={data.id} icon={<BiSolidShow size={20} />} />
  );
};

export const ListTestRun: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const ref = useRef<ExtendedTableLowCodeRef>(null);

  const isViewer = useAccess(id!);
  const isExternal = useIsExternal();
  const isReadOnly = isViewer || isExternal;

  const spec = useCustomSpec('/api/crud/test-run', {
    columns: [
      { key: 'created_by', label: 'Created By' },
      { key: 'title', label: 'Title' },
      { key: 'description', label: 'Description' },
      { key: 'assignee_id', label: 'Assigned To' },
      { key: 'created_at', label: 'Created Date' },
      { key: 'total_test_case', label: 'Test Cases' },
      { key: 'status', label: 'Status' },
    ],
  });

  const { data: projectData, isFetching: isProjectFetching } =
    ProjectService.useGet(id);
  const { data: assignedUsers } = TestRunService.useGetReleaseAndAssignee(id);

  const toArchive = () => navigate('archive');

  const optionAssignee = assignedUsers?.assign.map(user => ({
    label: user.label,
    value: user.label,
  }));

  const filterSpec: IFilterSpec[] = [
    { name: 'title', label: 'Title', type: 'text' },
    {
      name: 'assignee_to',
      label: 'Assigned To',
      type: 'option',
      options: optionAssignee,
    },
  ];

  if (isProjectFetching) {
    return (
      <Section>
        <Loading />
      </Section>
    );
  }

  return (
    <Section
      title={`Test Run - ${projectData?.title}`}
      actions={
        !isReadOnly ? (
          <div className="flex flex-row gap-3">
            <OutlineButton icon={<FaArchive />} onClick={toArchive}>
              Archive
            </OutlineButton>
            <CreateButton label="Add Test Run" />
          </div>
        ) : (
          <></>
        )
      }
    >
      <ExtendedTableLowCode
        ref={ref}
        defaultFilter={{
          project_id: id,
          is_archive: 'false',
        }}
        showFilter
        filterSpec={filterSpec}
        defaultSort="created_at"
        defaultSortOrder="desc"
        spec={spec}
        customButtonCreate={HideCustomCreateButton}
        customActionCell={data => (
          <CustomActionCell
            data={data as ITestRunSchema}
            isReadOnly={isReadOnly}
            tableRef={ref}
          />
        )}
        customCell={CustomCell}
      />
    </Section>
  );
};
